import { Trans } from '@lingui/react/macro'
import {
  Alert,
  Button,
  CardMedia,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { filter, firstValueFrom } from 'rxjs'
import { Box3, Vector3 } from 'three'
import { v4 } from 'uuid'

import { useAnalyticsService, useLogger, useMountGuard } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, LoadingButton, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { useCustomerProperties } from '../shopify/web-component-properties.js'
import { analyticsEvents } from '../systems/analytics.js'
import { configuratorInterface } from '../systems/configurator-interface.js'
import { FORM_ID, FinalizeForm, formSchema } from './FinalizeForm.js'

interface FinalizeDialogContentProps {
  controls: ReactNode
  hasError: boolean
  onSubmit: (data: unknown) => void
}

const FinalizeDialogContent = (props: FinalizeDialogContentProps) => {
  const { controls, hasError, onSubmit } = props

  return (
    <>
      <DialogTitle>
        <Trans>Get full details of your configuration</Trans>
      </DialogTitle>
      <DialogContent>
        <Typography>
          <Trans>
            Please provide your email address to receive the configuration details, links to share, PDF and generated
            image!
          </Trans>
        </Typography>
        <FinalizeForm onSubmit={onSubmit} />
        {hasError && (
          <Alert severity="error">
            <Trans>
              Configuration details could not be sent. Please try again later. If the issue persists, contact support.
            </Trans>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>{controls}</DialogActions>
    </>
  )
}

interface SuccessDialogContentProps {
  finalizationUuid: string
  onBack: () => void
}

const SuccessDialogContent = (props: SuccessDialogContentProps) => {
  const { finalizationUuid, onBack } = props

  const { configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const { customerEmail } = useCustomerProperties()
  const logger = useLogger()
  const postedFinalizationUuid = useRef<string | null>(null)

  const getResult = useCallback(async () => {
    return apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid, {
      pollingTimeout: 20 * 60 * 1000,
    })
  }, [apiClient, configurationId, finalizationUuid])

  const { data, isError } = useQuery({
    queryKey: ['finalize', finalizationUuid],
    queryFn: getResult,
  })

  useEffect(() => {
    if (!data?.pdfLink || postedFinalizationUuid.current === finalizationUuid || customerEmail === undefined) {
      return
    }

    postedFinalizationUuid.current = finalizationUuid
    try {
      window.parent.postMessage({ event: 'awr_3d_quote_submitted', cid: configurationId }, window.location.origin)
    } catch (error) {
      logger.warn(
        'Could not notify the Shopify page of a completed quote:',
        {},
        error instanceof Error ? error : new Error(String(error))
      )
    }
  }, [configurationId, customerEmail, data?.pdfLink, finalizationUuid, logger])

  return (
    <>
      <CardMedia
        sx={{
          blockSize: 160,
          color: 'success.main',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <PhxIcon name="CheckCircle" style={{ fontSize: 64 }} />
      </CardMedia>
      <DialogTitle>
        <Trans>Successfully Submitted</Trans>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          <Trans>All information including links to share, PDF and generated image will be sent to your email!</Trans>
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Stack direction="column" spacing={2}>
          {isError && (
            <Alert severity="error">
              <Trans>
                Configuration resources could not be generated. Please try again later. If the issue persists, contact
                support.
              </Trans>
            </Alert>
          )}
          {!data && (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Waiting for resources to be generated...</Trans>
              </Typography>
              <LinearProgress color="secondary" />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" variant="text" onClick={onBack}>
          <Trans>Back to Summary</Trans>
        </Button>
        <Button
          href={data?.pdfLink}
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          variant="contained"
          disabled={!data}
        >
          <Trans>Download PDF</Trans>
        </Button>
      </DialogActions>
    </>
  )
}

interface FinalizePageProps extends DialogProps {
  onBack: () => void
}

export const FinalizePage = (props: FinalizePageProps) => {
  const { onBack, open } = props

  const [finalizationUuid, setFinalizationUuid] = useState<string | null>(null)

  const { $price } = configuratorInterface.useRootEntityState()
  const { configurationId } = configuratorInterface.useDomain()
  const { imageRenderingSystem, canvasSystem } = configuratorInterface.useConfigurator()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const isMounted = useMountGuard()
  const logger = useLogger()
  const analyticsService = useAnalyticsService()

  const boundingBox = configuratorInterface.useRootEntitySelector(e => {
    return e.$boundingBox
  }, [])

  const generateImages = useCallback(
    async (finalizationUuid: string) => {
      const fieldOfView = 50
      const width = 1024
      const height = 768

      await imageRenderingSystem.generateImagesUsingOriginalCanvas(finalizationUuid, [
        {
          name: 'sketching',
          width,
          height,
          setupCamera: async (_, controls) => {
            const bigEnoughTimestep = 10000
            const xDistance = boundingBox.max.x - boundingBox.min.x

            await controls.fitToBox(
              new Box3(
                new Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.min.z),
                new Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z)
              ),
              false
            )

            controls.update(bigEnoughTimestep)

            const goodEnoughDistanceToCamera = (1.2 * xDistance) / Math.tan((fieldOfView * Math.PI) / 180)

            await controls.rotateAzimuthTo(-0.2, false)
            await controls.rotatePolarTo(1.2, false)
            await controls.dollyTo(goodEnoughDistanceToCamera, false)

            controls.update(bigEnoughTimestep)

            await new Promise(resolve => setTimeout(resolve, 50))
          },
        },
      ])
    },
    [imageRenderingSystem, boundingBox]
  )

  const frontendData = configuratorInterface.useFrontendData()

  const finalize = useCallback(
    async (data: unknown) => {
      const finalizationUuid = v4()

      const parsedData = formSchema.parse(data)

      await firstValueFrom(canvasSystem.firstLoadFinished$.pipe(filter(v => v)))

      // TODO: this isn't perfect and may result in empty image sometimes => but let's deal with that later
      await new Promise(resolve => setTimeout(resolve, 200))

      await Promise.all([configurationPersistSystem.save(), generateImages(finalizationUuid)])

      await apiClient.finalize(
        configurationId,
        finalizationUuid,
        {
          emailAddress: parsedData.emailAddress,
        },
        undefined,
        frontendData
      )

      analyticsService.push(analyticsEvents.ConfiguratorFinalized($price))

      if (isMounted()) {
        setFinalizationUuid(finalizationUuid)
      }
    },
    [
      apiClient,
      configurationPersistSystem,
      generateImages,
      configurationId,
      isMounted,
      frontendData,
      canvasSystem,
      analyticsService,
      $price,
    ]
  )

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationKey: ['finalize'],
    mutationFn: finalize,
    onError(error) {
      logger.error('Finalization failed:', {}, error)
    },
  })

  return (
    <Dialog.Root
      isOpen={open}
      onClose={onBack}
      sx={{ maxWidth: 'unset', width: `min(100%, ${isSuccess ? 560 : 640}px)` }}
    >
      {isSuccess && finalizationUuid && <SuccessDialogContent finalizationUuid={finalizationUuid} onBack={onBack} />}
      {!isSuccess && (
        <FinalizeDialogContent
          controls={
            <>
              {/* <Button
                color="inherit"
                variant="text"
                onClick={() => {
                  generateImages(v4())
                }}
              >
                Render
              </Button> */}
              <Button color="inherit" variant="text" onClick={onBack}>
                <Trans>Back to Summary</Trans>
              </Button>
              <LoadingButton type="submit" form={FORM_ID} variant="contained" busy={isPending}>
                <Trans>Finish</Trans>
              </LoadingButton>
            </>
          }
          hasError={isError}
          onSubmit={mutate}
        />
      )}
    </Dialog.Root>
  )
}
